/**
 * Company Data Models
 * Based on the Company DTO structure from Spring Boot backend
 */

const readBool = (data, camel, snake) => {
  const raw = data[camel] ?? data[snake];
  if (raw === undefined || raw === null || raw === '') return undefined;
  if (raw === true || raw === false) return raw;
  const s = String(raw).trim().toLowerCase();
  if (s === 'true' || s === '1' || s === 'yes') return true;
  if (s === 'false' || s === '0' || s === 'no') return false;
  return Boolean(raw);
};

const readNum = (data, camel, snake) => {
  const raw = data[camel] ?? data[snake];
  if (raw === '' || raw == null) return undefined;
  const num = Number(raw);
  return Number.isFinite(num) ? num : undefined;
};

const readStr = (data, camel, snake) => {
  const raw = data[camel] ?? data[snake];
  return raw == null ? '' : String(raw);
};

const readStrArray = (data, camel, snake) => {
  const raw = data[camel] ?? data[snake];
  if (Array.isArray(raw)) return raw.map((v) => String(v));
  if (typeof raw === 'string' && raw.trim()) {
    return raw.split(/[;,]/).map((v) => v.trim()).filter(Boolean);
  }
  return [];
};

// Company Address DTO (unified user address + supplier facility)
export class CompanyAddressDTO {
  constructor(data = {}) {
    const addressId = readStr(data, 'addressId', 'address_id') || readStr(data, 'id', 'id');
    this.addressId = addressId;
    this.id = addressId;

    this.tenantId = readStr(data, 'tenantId', 'tenant_id');
    this.ticker =
      readStr(data, 'ticker', 'ticker') ||
      readStr(data, 'supplierId', 'supplier_id') ||
      readStr(data, 'supplierTicker', 'supplier_ticker');
    /** @deprecated Use ticker — kept for internal reads of legacy rows */
    this.supplierId = this.ticker;
    this.facilityId = readStr(data, 'facilityId', 'facility_id');
    this.facilityName =
      readStr(data, 'facilityName', 'facility_name') || readStr(data, 'name', 'name');

    this.streetAddress =
      readStr(data, 'streetAddress', 'street_address') || readStr(data, 'address', 'address');
    this.city = readStr(data, 'city', 'city');
    this.state = readStr(data, 'state', 'state') || readStr(data, 'stateProvince', 'state_province');
    this.stateProvince = this.state;
    this.region = readStr(data, 'region', 'region');
    this.postalCode = readStr(data, 'postalCode', 'postal_code');
    this.country = readStr(data, 'country', 'country');
    this.location = readStr(data, 'location', 'location');

    this.addressType = readStr(data, 'addressType', 'address_type');
    this.facilityType = readStr(data, 'facilityType', 'facility_type');
    this.isPrimary =
      readBool(data, 'isPrimary', 'is_primary') !== undefined
        ? readBool(data, 'isPrimary', 'is_primary')
        : false;

    this.capacityUnits = readStr(data, 'capacityUnits', 'capacity_units');
    this.capacityValue = readStr(data, 'capacityValue', 'capacity_value');
    this.capacityShare = readNum(data, 'capacityShare', 'capacity_share');
    this.operationalStatus = readStr(data, 'operationalStatus', 'operational_status');
    this.headcount = readNum(data, 'headcount', 'headcount');
    this.hsCodes = readStrArray(data, 'hsCodes', 'hs_codes');
    this.since = readStr(data, 'since', 'since');

    this.certifications = readStrArray(data, 'certifications', 'certifications');
    this.uflpaTerritory = readBool(data, 'uflpaTerritory', 'uflpa_territory');
    this.exportControlZone = readStr(data, 'exportControlZone', 'export_control_zone');
    this.lastAuditDate = readStr(data, 'lastAuditDate', 'last_audit_date');
    this.auditOutcome = readStr(data, 'auditOutcome', 'audit_outcome');

    this.insuranceCoverage = readBool(data, 'insuranceCoverage', 'insurance_coverage');
    this.naturalHazardScore = readNum(data, 'naturalHazardScore', 'natural_hazard_score');
    this.singleSiteRisk = readBool(data, 'singleSiteRisk', 'single_site_risk');

    this.leadTimeDays = readNum(data, 'leadTimeDays', 'lead_time_days');
    this.primaryPort = readStr(data, 'primaryPort', 'primary_port');
    this.freightMode = readStr(data, 'freightMode', 'freight_mode');
    this.avgTransitDays = readNum(data, 'avgTransitDays', 'avg_transit_days');

    this.annualSpendShare = readNum(data, 'annualSpendShare', 'annual_spend_share');

    this.lastVerifiedAt = readStr(data, 'lastVerifiedAt', 'last_verified_at');
    this.verifiedBy = readStr(data, 'verifiedBy', 'verified_by');
    this.dataSource = readStr(data, 'dataSource', 'data_source');
    this.confidenceRating = readStr(data, 'confidenceRating', 'confidence_rating');

    this.latitude = readNum(data, 'latitude', 'latitude') ?? readNum(data, 'lat', 'lat');
    this.longitude = readNum(data, 'longitude', 'longitude') ?? readNum(data, 'lng', 'lng');

    this.addedDate = readStr(data, 'addedDate', 'added_date') || undefined;
  }

  toJSON() {
    const payload = {
      address_id: this.addressId || undefined,
      tenant_id: this.tenantId || undefined,
      ticker: this.ticker || undefined,
      facility_id: this.facilityId || undefined,
      facility_name: this.facilityName || undefined,
      street_address: this.streetAddress || undefined,
      city: this.city || undefined,
      state: this.state || undefined,
      region: this.region || undefined,
      postal_code: this.postalCode || undefined,
      country: this.country || undefined,
      location: this.location || undefined,
      address_type: this.addressType || undefined,
      facility_type: this.facilityType || undefined,
      is_primary: this.isPrimary,
      capacity_units: this.capacityUnits || undefined,
      capacity_value: this.capacityValue || undefined,
      capacity_share: this.capacityShare,
      operational_status: this.operationalStatus || undefined,
      headcount: this.headcount,
      hs_codes: this.hsCodes?.length ? this.hsCodes : undefined,
      since: this.since || undefined,
      certifications: this.certifications?.length ? this.certifications : undefined,
      uflpa_territory: this.uflpaTerritory,
      export_control_zone: this.exportControlZone || undefined,
      last_audit_date: this.lastAuditDate || undefined,
      audit_outcome: this.auditOutcome || undefined,
      insurance_coverage: this.insuranceCoverage,
      natural_hazard_score: this.naturalHazardScore,
      single_site_risk: this.singleSiteRisk,
      lead_time_days: this.leadTimeDays,
      primary_port: this.primaryPort || undefined,
      freight_mode: this.freightMode || undefined,
      avg_transit_days: this.avgTransitDays,
      annual_spend_share: this.annualSpendShare,
      last_verified_at: this.lastVerifiedAt || undefined,
      verified_by: this.verifiedBy || undefined,
      data_source: this.dataSource || undefined,
      confidence_rating: this.confidenceRating || undefined,
    };

    if (this.latitude != null) payload.latitude = this.latitude;
    if (this.longitude != null) payload.longitude = this.longitude;
    if (this.addedDate) payload.added_date = this.addedDate;

    return Object.fromEntries(
      Object.entries(payload).filter(([, value]) => value !== undefined && value !== '')
    );
  }

  /** Flat row for tenant UI tables (canonical + legacy keys). */
  toTableRow() {
    return {
      address_id: this.addressId,
      id: this.addressId,
      tenant_id: this.tenantId,
      ticker: this.ticker,
      supplier_ticker: this.ticker,
      facility_id: this.facilityId,
      facility_name: this.facilityName,
      street_address: this.streetAddress,
      address: this.streetAddress,
      city: this.city,
      state: this.state,
      region: this.region,
      postal_code: this.postalCode,
      country: this.country,
      location: this.location,
      address_type: this.addressType,
      facility_type: this.facilityType,
      is_primary: this.isPrimary ? 'true' : 'false',
      capacity_units: this.capacityUnits,
      capacity_value: this.capacityValue,
      capacity_share: this.capacityShare != null ? String(this.capacityShare) : '',
      operational_status: this.operationalStatus,
      headcount: this.headcount != null ? String(this.headcount) : '',
      hs_codes: this.hsCodes.join('; '),
      since: this.since,
      certifications: this.certifications.join('; '),
      uflpa_territory: this.uflpaTerritory === true ? 'true' : this.uflpaTerritory === false ? 'false' : '',
      export_control_zone: this.exportControlZone,
      last_audit_date: this.lastAuditDate,
      audit_outcome: this.auditOutcome,
      insurance_coverage:
        this.insuranceCoverage === true ? 'true' : this.insuranceCoverage === false ? 'false' : '',
      natural_hazard_score:
        this.naturalHazardScore != null ? String(this.naturalHazardScore) : '',
      single_site_risk:
        this.singleSiteRisk === true ? 'true' : this.singleSiteRisk === false ? 'false' : '',
      lead_time_days: this.leadTimeDays != null ? String(this.leadTimeDays) : '',
      primary_port: this.primaryPort,
      freight_mode: this.freightMode,
      avg_transit_days: this.avgTransitDays != null ? String(this.avgTransitDays) : '',
      annual_spend_share: this.annualSpendShare != null ? String(this.annualSpendShare) : '',
      last_verified_at: this.lastVerifiedAt,
      verified_by: this.verifiedBy,
      data_source: this.dataSource,
      confidence_rating: this.confidenceRating,
      latitude: this.latitude != null ? String(this.latitude) : '',
      longitude: this.longitude != null ? String(this.longitude) : '',
    };
  }

  getFullAddress() {
    const parts = [
      this.streetAddress,
      this.city,
      this.state,
      this.postalCode,
      this.country,
    ].filter((part) => part && String(part).trim());

    return parts.join(', ');
  }
}

// Company Type DTO
export class CompanyTypeDTO {
  constructor(data = {}) {
    this.type = data.type || '';
    this.description = data.description || '';
    this.category = data.category || '';
    this.isActive = data.isActive !== undefined ? data.isActive : (data.is_active !== undefined ? data.is_active : true);
  }

  toJSON() {
    return {
      type: this.type,
      description: this.description,
      category: this.category,
      isActive: this.isActive
    };
  }

  validate() {
    const errors = [];
    
    if (!this.type) {
      errors.push('Company type is required');
    }
    
    return errors;
  }
}

// Company Info DTO (Simplified company information)
export class CompanyInfoDTO {
  constructor(data = {}) {
    this.ticker = data.ticker || '';
    this.name = data.name || '';
    this.industry = data.industry || '';
    this.country = data.country || '';
    this.overallRiskScore = data.overallRiskScore || data.overall_risk_score || 0;
  }

  toJSON() {
    return {
      ticker: this.ticker,
      name: this.name,
      industry: this.industry,
      country: this.country,
      overallRiskScore: this.overallRiskScore
    };
  }

  getRiskScoreColor() {
    if (this.overallRiskScore >= 80) return { text: 'text-red-600', bg: 'bg-red-50' };
    if (this.overallRiskScore >= 60) return { text: 'text-orange-600', bg: 'bg-orange-50' };
    if (this.overallRiskScore >= 40) return { text: 'text-yellow-600', bg: 'bg-yellow-50' };
    if (this.overallRiskScore >= 20) return { text: 'text-blue-600', bg: 'bg-blue-50' };
    return { text: 'text-green-600', bg: 'bg-green-50' };
  }

  validate() {
    const errors = [];
    
    if (!this.ticker) {
      errors.push('Ticker is required');
    }
    
    if (!this.name) {
      errors.push('Company name is required');
    }
    
    if (!this.industry) {
      errors.push('Industry is required');
    }
    
    if (this.overallRiskScore < 0 || this.overallRiskScore > 100) {
      errors.push('Overall risk score must be between 0 and 100');
    }
    
    return errors;
  }
}

// News Article DTO
export class NewsArticleDTO {
  constructor(data = {}) {
    this.articleId = data.article_id ?? data.id ?? '';
    this.title = data.title || '';
    this.description = data.description || '';
    this.content = data.content || '';
    this.url = data.url || '';
    this.source = data.source || '';
    this.publishedAt = data.published_at || new Date().toISOString();
    this.sentiment = data.sentiment || 'neutral'; // positive, negative, neutral
    this.relevanceScore = data.relevance_score || 0;
    this.riskMatches = data.risk_matches || [];
    this.keywords = data.keywords || [];
  }

  toJSON() {
    return {
      article_id: this.articleId,
      title: this.title,
      description: this.description,
      content: this.content,
      url: this.url,
      source: this.source,
      published_at: this.publishedAt,
      sentiment: this.sentiment,
      relevance_score: this.relevanceScore,
      risk_matches: this.riskMatches,
      keywords: this.keywords
    };
  }

  getSentimentColor() {
    switch (this.sentiment.toLowerCase()) {
      case 'positive': return { text: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' };
      case 'negative': return { text: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' };
      default: return { text: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200' };
    }
  }

  getFormattedDate() {
    return new Date(this.publishedAt).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}

// Risk Score DTO
export class RiskScoreDTO {
  constructor(data = {}) {
    this.riskId = data.riskId || data.risk_id || '';
    this.riskName = data.riskName || data.risk_name || '';
    this.category = data.category || '';
    this.score = data.score || 0; // 0-100
    this.confidence = data.confidence || 0; // 0-100
    this.impactLevel = data.impactLevel || data.impact_level || 'medium'; // low, medium, high
    this.reasoning = data.reasoning || '';
    this.evidence = data.evidence || [];
    this.mitigationSuggestions = data.mitigationSuggestions || data.mitigation_suggestions || [];
    this.contributingNewsArticles = data.contributingNewsArticles || data.contributing_news_articles || [];
    this.lastUpdated = data.lastUpdated || data.last_updated || new Date().toISOString();
    
    // News ID arrays from backend
    this.contributingNewsIds = data.contributingNewsIds || data.contributing_news_ids || [];
    this.supportingNewsIds = data.supportingNewsIds || data.supporting_news_ids || [];
    this.criticalNewsIds = data.criticalNewsIds || data.critical_news_ids || [];
    this.highImpactNewsIds = data.highImpactNewsIds || data.high_impact_news_ids || [];
    this.positiveNewsIds = data.positiveNewsIds || data.positive_news_ids || [];
    this.negativeNewsIds = data.negativeNewsIds || data.negative_news_ids || [];
    this.neutralNewsIds = data.neutralNewsIds || data.neutral_news_ids || [];
    this.recentNewsIds = data.recentNewsIds || data.recent_news_ids || [];
  }

  toJSON() {
    return {
      risk_id: this.riskId,
      risk_name: this.riskName,
      category: this.category,
      score: this.score,
      confidence: this.confidence,
      impact_level: this.impactLevel,
      reasoning: this.reasoning,
      evidence: this.evidence,
      mitigation_suggestions: this.mitigationSuggestions,
      contributing_news_articles: this.contributingNewsArticles,
      last_updated: this.lastUpdated,
      contributing_news_ids: this.contributingNewsIds,
      supporting_news_ids: this.supportingNewsIds,
      critical_news_ids: this.criticalNewsIds,
      high_impact_news_ids: this.highImpactNewsIds,
      positive_news_ids: this.positiveNewsIds,
      negative_news_ids: this.negativeNewsIds,
      neutral_news_ids: this.neutralNewsIds,
      recent_news_ids: this.recentNewsIds
    };
  }

  getScoreColor() {
    if (this.score >= 80) return { text: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' };
    if (this.score >= 60) return { text: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' };
    if (this.score >= 40) return { text: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200' };
    return { text: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' };
  }

  getImpactLevelColor() {
    switch (this.impactLevel.toLowerCase()) {
      case 'high': return { text: 'text-red-700', bg: 'bg-red-100', border: 'border-red-300' };
      case 'medium': return { text: 'text-yellow-700', bg: 'bg-yellow-100', border: 'border-yellow-300' };
      case 'low': return { text: 'text-green-700', bg: 'bg-green-100', border: 'border-green-300' };
      default: return { text: 'text-gray-700', bg: 'bg-gray-100', border: 'border-gray-300' };
    }
  }

  getConfidenceColor() {
    if (this.confidence >= 80) return 'text-green-600';
    if (this.confidence >= 60) return 'text-yellow-600';
    return 'text-red-600';
  }
  
  // Get all news IDs associated with this risk
  getAllNewsIds() {
    return [
      ...this.contributingNewsIds,
      ...this.supportingNewsIds,
      ...this.criticalNewsIds,
      ...this.highImpactNewsIds,
      ...this.positiveNewsIds,
      ...this.negativeNewsIds,
      ...this.neutralNewsIds,
      ...this.recentNewsIds
    ].filter((id, index, self) => self.indexOf(id) === index); // Remove duplicates
  }
  
  // Check if this risk has any associated news
  hasNewsReferences() {
    return this.getAllNewsIds().length > 0;
  }
  
  // Get count of news by sentiment
  getNewsSentimentCounts() {
    return {
      positive: this.positiveNewsIds.length,
      negative: this.negativeNewsIds.length,
      neutral: this.neutralNewsIds.length,
      total: this.getAllNewsIds().length
    };
  }
}

// Risk Analysis DTO
export class RiskAnalysisDTO {
  constructor(data = {}) {
    try {
      this.analysisId = data.analysisId || data.analysis_id || '';
      this.companyName = data.companyName || data.company_name || '';
      this.ticker = data.ticker || '';
      this.industry = data.industry || '';
      this.analysisDate = data.analysisDate || data.analysis_date || new Date().toISOString();
      this.overallRiskScore = data.overallRiskScore || data.overall_risk_score || 0;
      this.supplyChainRiskScore = data.supplyChainRiskScore || data.supply_chain_risk_score || 0;
      this.industryRiskScore = data.industryRiskScore || data.industry_risk_score || 0;
      this.newsSentimentScore = data.newsSentimentScore || data.news_sentiment_score || 0;
      
      this.individualRiskScores = (data.individualRiskScores || data.individual_risk_scores || [])
        .map(score => {
          return new RiskScoreDTO(score);
        });
      
      this.analysisSummary = data.analysisSummary || data.analysis_summary || '';
      this.keyFindings = data.keyFindings || data.key_findings || [];
      this.riskTrends = data.riskTrends || data.risk_trends || [];
      this.recommendations = data.recommendations || [];
      this.analyzedNewsCount = data.analyzedNewsCount || data.analyzed_news_count || 0;
      this.positiveNewsCount = data.positiveNewsCount || data.positive_news_count || 0;
      this.negativeNewsCount = data.negativeNewsCount || data.negative_news_count || 0;
      this.neutralNewsCount = data.neutralNewsCount || data.neutral_news_count || 0;
      this.analyzedNewsIds = data.analyzedNewsIds || data.analyzed_news_ids || [];
      this.newsAnalysisSummary = data.newsAnalysisSummary || data.news_analysis_summary;
      this.analysisConfidence = data.analysisConfidence || data.analysis_confidence || 0;
      this.dataQualityScore = data.dataQualityScore || data.data_quality_score || 0;
      
    } catch (error) {
      throw error;
    }
  }

  toJSON() {
    return {
      analysisId: this.analysisId,
      companyName: this.companyName,
      ticker: this.ticker,
      industry: this.industry,
      analysisDate: this.analysisDate,
      overallRiskScore: this.overallRiskScore,
      supplyChainRiskScore: this.supplyChainRiskScore,
      industryRiskScore: this.industryRiskScore,
      newsSentimentScore: this.newsSentimentScore,
      individualRiskScores: this.individualRiskScores.map(score => score.toJSON()),
      analysisSummary: this.analysisSummary,
      keyFindings: this.keyFindings,
      riskTrends: this.riskTrends,
      recommendations: this.recommendations,
      analyzedNewsCount: this.analyzedNewsCount,
      positiveNewsCount: this.positiveNewsCount,
      negativeNewsCount: this.negativeNewsCount,
      neutralNewsCount: this.neutralNewsCount,
      analyzedNewsIds: this.analyzedNewsIds,
      newsAnalysisSummary: this.newsAnalysisSummary,
      analysisConfidence: this.analysisConfidence,
      dataQualityScore: this.dataQualityScore
    };
  }

  getOverallRiskLevel() {
    if (this.overallRiskScore >= 80) return 'Critical';
    if (this.overallRiskScore >= 60) return 'High';
    if (this.overallRiskScore >= 40) return 'Medium';
    if (this.overallRiskScore >= 20) return 'Low';
    return 'Very Low';
  }

  getOverallRiskColor() {
    if (this.overallRiskScore >= 80) return { text: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' };
    if (this.overallRiskScore >= 60) return { text: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' };
    if (this.overallRiskScore >= 40) return { text: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200' };
    if (this.overallRiskScore >= 20) return { text: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' };
    return { text: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' };
  }

  getFormattedAnalysisDate() {
    return new Date(this.analysisDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getNewsSentimentColor() {
    const total = this.positiveNewsCount + this.negativeNewsCount + this.neutralNewsCount;
    if (total === 0) return { text: 'text-gray-600', bg: 'bg-gray-50' };
    
    const sentiment = (this.positiveNewsCount - this.negativeNewsCount) / total;
    if (sentiment > 0.1) return { text: 'text-green-600', bg: 'bg-green-50' };
    if (sentiment < -0.1) return { text: 'text-red-600', bg: 'bg-red-50' };
    return { text: 'text-gray-600', bg: 'bg-gray-50' };
  }

  getTopRiskScores(limit = 5) {
    return this.individualRiskScores
      .sort((a, b) => b.score - a.score)
      .slice(0, limit);
  }

  getRiskScoresByCategory() {
    const categories = {};
    this.individualRiskScores.forEach(risk => {
      if (!categories[risk.category]) {
        categories[risk.category] = [];
      }
      categories[risk.category].push(risk);
    });
    return categories;
  }
}

// Main Company DTO
export class CompanyDTO {
  constructor(data = {}) {
    // Basic company information (ticker may be returned as company_id by some APIs)
    this.ticker = (
      data.ticker ||
      data.id ||
      data.company_id ||
      data.supplier_id ||
      data.supplierId ||
      ''
    )
      .toString()
      .trim();
    this.name = data.name || '';
    this.description = data.description || '';
    this.industry = data.industry || '';
    this.keywords = data.keywords || [];
    this.supplyChainRisks = data.supply_chain_risks || data.supplyChainRisks || [];
    this.riskCategories = data.risk_categories || data.riskCategories || [];

    // Supplier fields (unified Company model)
    const tierRaw = data.tier ?? data.supplier_tier;
    const tierStr = tierRaw != null && tierRaw !== '' ? String(tierRaw).trim() : '';
    const tierMatch = tierStr.match(/^tier\s*([123])$/i) || tierStr.match(/^([123])$/);
    this.tier = tierMatch ? tierMatch[1] : tierStr && /^[123]$/.test(tierStr) ? tierStr : '';
    this.tenantId = data.tenant_id || data.tenantId || '';
    this.legalName = data.legal_name || data.legalName || '';
    this.dunsNumber = data.duns_number || data.dunsNumber || '';
    this.region = data.region || '';
    this.categories = Array.isArray(data.categories)
      ? data.categories
      : typeof data.categories === 'string' && data.categories.trim()
        ? data.categories.split(/[;,]/).map((c) => c.trim()).filter(Boolean)
        : [];
    this.annualSpend = data.annual_spend ?? data.annualSpend ?? '';
    this.criticalityScore = data.criticality_score ?? data.criticalityScore;
    this.isSoleSource = data.is_sole_source ?? data.isSoleSource;
    this.isStrategic = data.is_strategic ?? data.isStrategic;
    this.uflpaFlag = data.uflpa_flag ?? data.uflpaFlag;
    this.onboardedAt = data.onboarded_at || data.onboardedAt || '';
    this.portalVerified = data.portal_verified ?? data.portalVerified;
    this.dataSource = data.data_source || data.dataSource || '';
    this.confidenceRating = data.confidence_rating || data.confidenceRating || '';

    // User management fields
    this.isUserMade = data.is_user_made || false;
    this.userId = data.user_id || '';

    // Timestamps
    this.createdAt = data.created_at || new Date().toISOString();
    this.updatedAt = data.updated_at || new Date().toISOString();
    this.newsUpdateTimestamp = data.news_update_timestamp || null;
    this.riskAnalysisTimestamp = data.risk_analysis_timestamp || null;

    // Risk analysis data (dual storage approach)
    if (data.latest_risk_analysis) {
      try {
        this.latestRiskAnalysis = new RiskAnalysisDTO(data.latest_risk_analysis);
      } catch (error) {
        this.latestRiskAnalysis = null;
      }
    } else if (data.latestRiskAnalysis) {
      try {
        this.latestRiskAnalysis = new RiskAnalysisDTO(data.latestRiskAnalysis);
      } catch (error) {
        this.latestRiskAnalysis = null;
      }
    } else if (data.risk_analysis_timestamp && data.overall_risk_score !== undefined) {
      try {
        this.latestRiskAnalysis = new RiskAnalysisDTO({
          analysisId: `${data.ticker}_${data.risk_analysis_timestamp}`,
          companyName: data.name,
          ticker: data.ticker,
          industry: data.industry,
          analysisDate: data.risk_analysis_timestamp,
          overallRiskScore: data.overall_risk_score || 0,
          supplyChainRiskScore: data.supply_chain_risk_score || 0,
          industryRiskScore: data.industry_risk_score || 0,
          newsSentimentScore: data.news_sentiment_score || 0,
          individualRiskScores: data.individual_risk_scores || [],
          analysisSummary: data.analysis_summary || '',
          keyFindings: data.key_findings || [],
          riskTrends: data.risk_trends || [],
          recommendations: data.recommendations || [],
          analyzedNewsCount: data.analyzed_news_count || data.total_news_articles || 0,
          positiveNewsCount: data.positive_news_count || 0,
          negativeNewsCount: data.negative_news_count || 0,
          neutralNewsCount: data.neutral_news_count || 0,
          analyzedNewsIds: data.analyzed_news_ids || [],
          newsAnalysisSummary: data.news_analysis_summary,
          analysisConfidence: data.analysis_confidence || 0,
          dataQualityScore: data.data_quality_score || 0
        });
      } catch (error) {
        this.latestRiskAnalysis = null;
      }
    } else {
      this.latestRiskAnalysis = null;
    }

    // Individual risk score fields (for Firebase compatibility)
    // Use latestRiskAnalysis values if available, otherwise fall back to individual fields
    this.overallRiskScore = this.latestRiskAnalysis?.overallRiskScore || data.overall_risk_score || 0;
    this.supplyChainRiskScore = this.latestRiskAnalysis?.supplyChainRiskScore || data.supply_chain_risk_score || 0;
    this.industryRiskScore = this.latestRiskAnalysis?.industryRiskScore || data.industry_risk_score || 0;
    this.newsSentimentScore = this.latestRiskAnalysis?.newsSentimentScore || data.news_sentiment_score || 0;

    this.analyzedNewsIds = this.latestRiskAnalysis?.analyzedNewsIds || data.analyzed_news_ids || [];

    // News metrics - prefer latestRiskAnalysis values over individual fields
    this.totalNewsArticles = this.latestRiskAnalysis?.analyzedNewsCount || data.total_news_articles || 0;
    this.positiveNewsCount = this.latestRiskAnalysis?.positiveNewsCount || data.positive_news_count || 0;
    this.negativeNewsCount = this.latestRiskAnalysis?.negativeNewsCount || data.negative_news_count || 0;
    this.neutralNewsCount = this.latestRiskAnalysis?.neutralNewsCount || data.neutral_news_count || 0;

    // Company metadata
    this.marketCap = data.market_cap || 0;
    this.sector = data.sector || '';
    this.country = data.country || '';
    const websiteRaw = data.website ?? '';
    const websiteStr = String(websiteRaw).trim();
    const invalidWebsite = ['false', 'true', 'null', 'undefined', 'none', 'n/a'].includes(
      websiteStr.toLowerCase()
    );
    this.website = invalidWebsite ? '' : websiteStr;
    this.employeeCount = data.employee_count || 0;

    // User-added addresses
    this.userAddedAddresses = (data.user_added_addresses || [])
      .map(addr => new CompanyAddressDTO(addr));

    // News articles
    this.newsArticles = (data.news_articles || [])
      .map(article => new NewsArticleDTO(article));
  }

  toJSON() {
    return {
      ticker: this.ticker,
      name: this.name,
      description: this.description,
      industry: this.industry,
      keywords: this.keywords,
      supply_chain_risks: this.supplyChainRisks,
      risk_categories: this.riskCategories,
      tier: this.tier || undefined,
      tenant_id: this.tenantId || undefined,
      legal_name: this.legalName || undefined,
      duns_number: this.dunsNumber || undefined,
      region: this.region || undefined,
      categories: this.categories?.length ? this.categories : undefined,
      annual_spend: this.annualSpend || undefined,
      criticality_score: this.criticalityScore,
      is_sole_source: this.isSoleSource,
      is_strategic: this.isStrategic,
      uflpa_flag: this.uflpaFlag,
      onboarded_at: this.onboardedAt || undefined,
      portal_verified: this.portalVerified,
      data_source: this.dataSource || undefined,
      confidence_rating: this.confidenceRating || undefined,
      is_user_made: this.isUserMade,
      user_id: this.userId,
      created_at: this.createdAt,
      updated_at: this.updatedAt,
      news_update_timestamp: this.newsUpdateTimestamp,
      risk_analysis_timestamp: this.riskAnalysisTimestamp,
      latest_risk_analysis: this.latestRiskAnalysis?.toJSON() || null,
      overall_risk_score: this.overallRiskScore,
      supply_chain_risk_score: this.supplyChainRiskScore,
      industry_risk_score: this.industryRiskScore,
      news_sentiment_score: this.newsSentimentScore,
      analyzed_news_ids: this.analyzedNewsIds,
      total_news_articles: this.totalNewsArticles,
      positive_news_count: this.positiveNewsCount,
      negative_news_count: this.negativeNewsCount,
      neutral_news_count: this.neutralNewsCount,
      market_cap: this.marketCap,
      sector: this.sector,
      country: this.country,
      website: this.website,
      employee_count: this.employeeCount,
      user_added_addresses: this.userAddedAddresses.map(addr => addr.toJSON()),
      news_articles: this.newsArticles.map(article => article.toJSON())
    };
  }

  // Helper methods
  getFormattedMarketCap() {
    if (!this.marketCap || this.marketCap === 0) return 'N/A';
    
    const value = parseFloat(this.marketCap);
    if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
    if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    if (value >= 1e3) return `$${(value / 1e3).toFixed(2)}K`;
    return `$${value.toFixed(2)}`;
  }

  getFormattedEmployeeCount() {
    if (!this.employeeCount || this.employeeCount === 0) return 'N/A';
    
    const count = parseInt(this.employeeCount);
    if (count >= 1e6) return `${(count / 1e6).toFixed(1)}M`;
    if (count >= 1e3) return `${(count / 1e3).toFixed(1)}K`;
    return count.toString();
  }

  getRiskScoreColor(score) {
    if (score >= 80) return 'text-red-600';
    if (score >= 60) return 'text-orange-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-green-600';
  }

  getRiskScoreBgColor(score) {
    if (score >= 80) return 'bg-red-50';
    if (score >= 60) return 'bg-orange-50';
    if (score >= 40) return 'bg-yellow-50';
    return 'bg-green-50';
  }

  getNewsSentimentColor() {
    const positive = this.positiveNewsCount || 0;
    const negative = this.negativeNewsCount || 0;
    const total = positive + negative + (this.neutralNewsCount || 0);
    
    if (total === 0) return 'text-gray-600';
    
    const sentiment = (positive - negative) / total;
    if (sentiment > 0.1) return 'text-green-600';
    if (sentiment < -0.1) return 'text-red-600';
    return 'text-gray-600';
  }

  getNewsSentimentBgColor() {
    const positive = this.positiveNewsCount || 0;
    const negative = this.negativeNewsCount || 0;
    const total = positive + negative + (this.neutralNewsCount || 0);
    
    if (total === 0) return 'bg-gray-50';
    
    const sentiment = (positive - negative) / total;
    if (sentiment > 0.1) return 'bg-green-50';
    if (sentiment < -0.1) return 'bg-red-50';
    return 'bg-gray-50';
  }

  validate() {
    const errors = [];
    
    if (!this.ticker || this.ticker.length > 32) {
      errors.push('Ticker is required and must not exceed 32 characters');
    }
    
    if (!this.name || this.name.length > 255) {
      errors.push('Company name is required and must not exceed 255 characters');
    }
    
    if (!this.description || this.description.length > 1000) {
      errors.push('Description is required and must not exceed 1000 characters');
    }
    
    if (!this.industry) {
      errors.push('Industry is required');
    }
    
    if (this.overallRiskScore < 0 || this.overallRiskScore > 100) {
      errors.push('Overall risk score must be between 0 and 100');
    }
    
    if (this.supplyChainRiskScore < 0 || this.supplyChainRiskScore > 100) {
      errors.push('Supply chain risk score must be between 0 and 100');
    }
    
    if (this.industryRiskScore < 0 || this.industryRiskScore > 100) {
      errors.push('Industry risk score must be between 0 and 100');
    }
    
    if (this.newsSentimentScore < 0 || this.newsSentimentScore > 100) {
      errors.push('News sentiment score must be between 0 and 100');
    }
    
    if (this.website && !/^https?:\/\/.+/.test(this.website)) {
      errors.push('Website must be a valid URL');
    }
    
    return errors;
  }
}

// Risk Level Enum
export const RISK_LEVELS = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

// Risk Category Enum
export const RISK_CATEGORIES = {
  SUPPLY_CHAIN: 'supply_chain',
  FINANCIAL: 'financial',
  OPERATIONAL: 'operational',
  REGULATORY: 'regulatory',
  MARKET: 'market',
  TECHNOLOGY: 'technology',
  ENVIRONMENTAL: 'environmental',
  GEOPOLITICAL: 'geopolitical'
};

// Supply Chain Risk Types
export const SUPPLY_CHAIN_RISK_TYPES = {
  SUPPLIER_DEPENDENCY: 'supplier_dependency',
  LOGISTICS_DISRUPTION: 'logistics_disruption',
  RAW_MATERIAL_SHORTAGE: 'raw_material_shortage',
  QUALITY_ISSUES: 'quality_issues',
  CAPACITY_CONSTRAINTS: 'capacity_constraints',
  COST_VOLATILITY: 'cost_volatility',
  REGULATORY_CHANGES: 'regulatory_changes',
  CYBER_SECURITY: 'cyber_security'
};

// Model Factory Functions
export const createCompany = (data) => new CompanyDTO(data);
export const createCompanyAddress = (data) => new CompanyAddressDTO(data);
export const createCompanyType = (data) => new CompanyTypeDTO(data);
export const createCompanyInfo = (data) => new CompanyInfoDTO(data);
export const createRiskAnalysis = (data) => new RiskAnalysisDTO(data);
export const createNewsArticle = (data) => new NewsArticleDTO(data);
export const createRiskScore = (data) => new RiskScoreDTO(data);

// Utility Functions
export const formatMarketCap = (value) => {
  if (!value || value === 0) return 'N/A';
  
  const numValue = parseFloat(value);
  if (numValue >= 1e12) return `$${(numValue / 1e12).toFixed(2)}T`;
  if (numValue >= 1e9) return `$${(numValue / 1e9).toFixed(2)}B`;
  if (numValue >= 1e6) return `$${(numValue / 1e6).toFixed(2)}M`;
  if (numValue >= 1e3) return `$${(numValue / 1e3).toFixed(2)}K`;
  return `$${numValue.toFixed(2)}`;
};

export const formatEmployeeCount = (count) => {
  if (!count || count === 0) return 'N/A';
  
  const numCount = parseInt(count);
  if (numCount >= 1e6) return `${(numCount / 1e6).toFixed(1)}M`;
  if (numCount >= 1e3) return `${(numCount / 1e3).toFixed(1)}K`;
  return numCount.toString();
};

export const getRiskScoreColor = (score) => {
  if (score >= 80) return { text: 'text-red-600', bg: 'bg-red-50' };
  if (score >= 60) return { text: 'text-orange-600', bg: 'bg-orange-50' };
  if (score >= 40) return { text: 'text-yellow-600', bg: 'bg-yellow-50' };
  return { text: 'text-green-600', bg: 'bg-green-50' };
};

export const getNewsSentimentColor = (positive, negative, neutral) => {
  const total = positive + negative + neutral;
  if (total === 0) return { text: 'text-gray-600', bg: 'bg-gray-50' };
  
  const sentiment = (positive - negative) / total;
  if (sentiment > 0.1) return { text: 'text-green-600', bg: 'bg-green-50' };
  if (sentiment < -0.1) return { text: 'text-red-600', bg: 'bg-red-50' };
  return { text: 'text-gray-600', bg: 'bg-gray-50' };
};
